import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { landingRouteFor, rolesWithAccess } from '@/access/roleAccess'
import { roleDefinitionFor, type RoleKey } from '@/access/roles'
import { AppShell } from '@/components/px/AppShell'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'
import { useAppSelector } from '@/store'

/**
 * A destination this role cannot reach, refused LEGIBLY: it says what was
 * asked for, who is signed in, which roles could open it, and the way back.
 *
 * ⚠ It NEVER auto-navigates and never rewrites the URL (AC-1.4). A shared deep
 * link stays inspectable — that asymmetry with the session guard (which DOES
 * redirect) is deliberate: a missing session is a state to fix, so move the
 * visitor; a denied role is a fact to state, so show it where they asked.
 *
 * ⚠ It re-derives nothing. `rolesWithAccess` and `landingRouteFor` read the
 * SAME `ROLE_ACCESS` table `RoleGuarded` refused on, so the refusal and its
 * explanation can never disagree (`canAccess` stays the single enforcement
 * point).
 *
 * PX-W6.6 moved this page's copy out of `src/demo/content/` into the `common`
 * locale namespace: a refusal is real product behaviour, so its words are
 * product copy in EN and NL, not demo content.
 *
 * No `ConceptBadge` here — denial is a real behaviour, not a concept.
 */
export default function RoleBlockedPage({ requested }: { requested: string }) {
  const { t } = useTranslation('common')
  const role = useAppSelector(selectActiveRoleKey)
  // PX-W6.6 fix-back (merged review C1): the LOCALISED role label, never
  // `roleDefinitionFor(...).label`, whose client-side rows used to name the
  // demo client. Both this list and the sentence below read the same source.
  const roleLabel = (key: RoleKey): string => t(roleDefinitionFor(key).labelKey)
  const permitted = rolesWithAccess(requested).map(roleLabel)
  const landing = landingRouteFor(role)

  return (
    <AppShell
      moduleLabel={t('roleBlocked.moduleLabel')}
      breadcrumb={t('roleBlocked.breadcrumb')}
      contextPill={t('roleBlocked.contextPill')}
    >
      <div className="w-full max-w-3xl" data-testid="role-blocked">
        <h1 className="font-serif text-2xl font-semibold text-foreground">
          {t('roleBlocked.heading')}
        </h1>

        <div className="mt-4">
          <SectionLabel>{t('roleBlocked.requestedLabel')}</SectionLabel>
          <p className="mt-1 font-mono text-sm text-foreground">{requested}</p>
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          {t('roleBlocked.currentRole', { role: roleLabel(role) })}
        </p>

        <div className="mt-4">
          <SectionLabel>{t('roleBlocked.permittedLabel')}</SectionLabel>
          {permitted.length > 0 ? (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {permitted.map((label) => (
                <StatusPill key={label} tone="neutral">
                  {label}
                </StatusPill>
              ))}
            </div>
          ) : (
            <p className="mt-1 text-sm text-muted-foreground">{t('roleBlocked.nobody')}</p>
          )}
        </div>

        <Link
          to={landing}
          className="mt-6 inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t('roleBlocked.back', { route: landing })}
        </Link>
      </div>
    </AppShell>
  )
}
